import React, { useState } from 'react';
import {
    Box,
    TextField,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Switch,
    FormControlLabel,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';

// Custom styled switch to meet specific color requirements
const RejectedSwitch = styled(Switch)(({ theme }) => ({
    '& .MuiSwitch-switchBase': {
        color: 'white', // Thumb color when unchecked
        '&.Mui-checked': {
            color: 'white', // Thumb color when checked
            '& + .MuiSwitch-track': {
                backgroundColor: '#EE3B2B', // Background color when checked
            }
        }
    },
    '& .MuiSwitch-track': {
        backgroundColor: '#CCCCCC', // Background color when unchecked
    }
}));

// Custom styled form control label for the rejected switch
const RejectedFormControlLabel = styled(FormControlLabel)({
    '& .MuiFormControlLabel-label': {
        fontFamily: 'Roboto',
        fontWeight: 500,
        fontSize: '16px',
        lineHeight: '100%',
        letterSpacing: '0%',
        color: '#183059'
    }
});

// Styled TextField to match previous component's style
const StyledTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        width: '100%',
        maxWidth: '320px',
        height: '35px',
        borderRadius: '4px',
        backgroundColor: '#F2F2F3',
        border: '1px solid #414958',
        padding: '0',
        '& fieldset': {
            border: 'none',
        },
        '&:hover fieldset': {
            border: 'none',
        },
        '&.Mui-focused fieldset': {
            border: 'none',
        },
    },
    '& .MuiOutlinedInput-input': {
        padding: '8px 16px',
    },
    '& .MuiInputLabel-outlined': {
        transform: 'translate(16px, 8px) scale(1)',
    },
    '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
        transform: 'translate(14px, -6px) scale(0.75)',
        backgroundColor: '#F2F2F3',
        padding: '0 4px',
    },
    [theme.breakpoints.down('md')]: {
        maxWidth: '100%',
    },
}));

const StyledSelect = styled(Select)(({ theme }) => ({
    width: '202px',
    height: '35px',
    borderRadius: '4px',
    backgroundColor: '#F2F2F3',
    border: '1px solid #414958',
    '& .MuiOutlinedInput-notchedOutline': {
        border: 'none',
    },
    '& .MuiSelect-select': {
        padding: '8px 16px',
    },
    [theme.breakpoints.down('md')]: {
        width: '100%',
    },
}));
const StyledFormControl = styled(FormControl)(({ theme }) => ({
    '& .MuiInputLabel-outlined': {
        transform: 'translate(16px, 8px) scale(1)',
    },
    '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
        transform: 'translate(14px, -6px) scale(0.75)',
        backgroundColor: '#F2F2F3',
        padding: '0 4px',
    },
    [theme.breakpoints.down('md')]: {
        width: '100%',
    },
}));
const RequestFilter = ({
    organizations = [],
    cities = [],
    onSearchChange,
    onCountryChange,
    onCityChange,
    onToggleRejected
}) => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
    const isExtraSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const [searchTerm, setSearchTerm] = useState('');
    const [country, setCountry] = useState('');
    const [city, setCity] = useState('');
    const [showRejected, setShowRejected] = useState(false);

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        onSearchChange(value);
    };

    const handleCountryChange = (e) => {
        const value = e.target.value;
        setCountry(value);
        onCountryChange(value);
    };

    const handleCityChange = (e) => {
        const value = e.target.value;
        setCity(value);
        onCityChange(value);
    };

    const handleToggleRejected = (e) => {
        const checked = e.target.checked;
        setShowRejected(checked);
        onToggleRejected(checked);
    };

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: isSmallScreen ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: isSmallScreen ? 'stretch' : 'center',
            gap: isSmallScreen ? 2 : 0,
            mb: 2
        }}>
            {/* Left side: Search and Rejected Toggle */}
            <Box sx={{
                display: 'flex',
                flexDirection: isExtraSmallScreen ? 'column' : 'row',
                alignItems: 'center',
                gap: 2,
                width: isSmallScreen ? '100%' : 'auto'
            }}>
                {/* Search Input */}
                <StyledTextField
                    id="search-input"
                    placeholder="Search"
                    variant="outlined"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    sx={{
                        width: isExtraSmallScreen ? '100%' : 'auto',
                        fontFamily: "Roboto, sans-serif",
                        fontWeight: 400,
                        fontSize: "16px",
                        lineHeight: "18.75px",
                        letterSpacing: "0%",
                        color: "#1F242E",
                        "& input::placeholder": {
                            color: "#1F242E",
                            opacity: 1,
                        },
                    }}
                />

                {/* Rejected Toggle */}
                <RejectedFormControlLabel
                    control={
                        <RejectedSwitch
                            checked={showRejected}
                            onChange={handleToggleRejected}
                        />
                    }
                    label="Rejected Requests"
                />
            </Box>

            {/* Right side: Organization and City Dropdowns */}
            <Box sx={{
                display: 'flex',
                flexDirection: isSmallScreen ? 'column' : 'row',
                gap: 2,
                width: isSmallScreen ? '100%' : 'auto'
            }}>
                {/* Organization Dropdown */}
                <StyledFormControl
                    size="small"
                    sx={{
                        width: isExtraSmallScreen ? '100%' : 202,
                        minWidth: 150
                    }}
                >
                    <InputLabel sx={{
                        "& input::placeholder": {
                            color: "#1F242E",
                            opacity: 1,
                        },
                    }}>Organization</InputLabel>
                    <StyledSelect
                        value={country}
                        label="Organization"
                        onChange={handleCountryChange}
                    >
                        <MenuItem value="">All Organizations</MenuItem>
                        {organizations.map((org) => (
                            <MenuItem key={org} value={org}>{org}</MenuItem>
                        ))}
                    </StyledSelect>
                </StyledFormControl>

                {/* City Dropdown */}
                <StyledFormControl
                    size="small"
                    sx={{
                        width: isExtraSmallScreen ? '100%' : 202,
                        minWidth: 150
                    }}
                >
                    <InputLabel>City</InputLabel>
                    <StyledSelect
                        value={city}
                        label="City"
                        onChange={handleCityChange}
                    >
                        <MenuItem value="">All Cities</MenuItem>
                        {cities.map((c) => (
                            <MenuItem key={c} value={c}>{c}</MenuItem>
                        ))}
                    </StyledSelect>
                </StyledFormControl>
            </Box>
        </Box>
    );
};

export default RequestFilter;